import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from 'react-i18next';

import { ModalContext } from "components/ModalContext";

import { useDispatch, useSelector } from "react-redux";
import { updateFilter, setTemplateAction, onSelectionChange } from "store/actions/filter";
import { addSelectItems, updateTable, setCurrentTableStateAC } from "store/actions/table";
import { applyFiltesrs, clearColors, updateCategory } from "utils/builder/IScheduleLogic";
import { useDashboardState } from "components/DashboardContext";
import { getCurrentNamespace } from "utils/crossTabSync";
import "./SidebarProject.scss";

import { ReactComponent as FolderOpen } from 'static/icons/folder-open-01.svg'
import { ReactComponent as Save } from "static/icons/save.svg";

import UniversalButton from "components/uiComponents/UniversalButton";
import RadioListModal from "components/modals/RadioListModal";
import SaveTemplateModals from "components/modals/SaveTemplateModals";
import SidebarButtonDropdown from "components/sidebarComponents/SidebarButtonDropdown";
import SidebarDropdown from "components/sidebarComponents/SidebarDropdown";
import GoToPremiumModal from "components/modals/GoToPremiumModal";
import Tag from "components/uiComponents/Tag";
import ModalComponent from "../../modals/ModalComponent";
import TranslateModel from "components/TranslateModel";

import { checkPremium } from "../../../utils/helpers";
import AdvanceFilterTag from "../AdvanceFilterTag";
import { getFilterScheme, getTableColumns, getTemplateSetting, getTemporarySetting } from "store/selectors/templateSettings.js";
import useSetting from "./use-settings";
import { deleteColumnAC } from "store/actions/condition";
import AddParameterModal from "components/modals/AddParameterModal/AddParameterModal";

const SidebarProject = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();
  const { handleModal } = useContext(ModalContext);

  const [isPremiumModalVisible, setPremiumModalVisible] = useState(false);
  const [isOpenInitialDropdown, setInitDropdownIsOpen] = useState(null);

  const tableColumns = useSelector(getTableColumns)
  const filtersSchemes = useSelector(getFilterScheme)

  const { openSideBarHandler, removeTableColumn, addPropertyHandler, removePropertyGroupHandler } = useSetting();

  const templateSettings = useSelector(getTemplateSetting)
  const temporarySetting = useSelector(getTemporarySetting)
  const currentSettingColumnId = temporarySetting?.metaData?.name
  const selectedGroups = templateSettings.groups ?? []
  const selectedCategories = templateSettings?.categories ?? []
  const hasSelectedCategories = selectedCategories.length > 0

  const selectedProperties = useMemo(() => {
    return tableColumns.reduce(((acc, column) => {
      const { isCustomColumn, name } = column.metaData
      if (!isCustomColumn) {
        acc.push(name)
      }
      return acc
    }), [])
  },
    [tableColumns])
  const {
    templates,
    template,
    commonCategories,
    uncommonCategories,
    commonProperties,
    customProperties,
    uncommonProperties,
    commonGroupingOptions,
    uncommonGroupingOptions,
    templatesFolder,
    customPropertiesValues,
  } = useSelector((state) => state.viewer);

  const { subscreption } = useSelector((state) => state.user.info);

  const IS_PREMIUM = checkPremium(subscreption);

  const {
    setIsTableHidden,
    isModalLoaded,
    setIsModalLoaded,
    isTranslateModelEnabled,
    setIsTranslateModelEnabled,
    getLabelByIsTranslateModelEnabled
  } = useDashboardState();

  useEffect(() => {
    const requestForQuickCounterData = async () => {
      const res = await applyFiltesrs(filtersSchemes)
      if (res?.quickCounterData) {
        dispatch(onSelectionChange(res.quickCounterData))
      }
    }
    requestForQuickCounterData(filtersSchemes)
  }, [dispatch, filtersSchemes])

  const dispatchFilters = (callback, type, ...data) => {
    dispatch(
      updateFilter(
        () => callback(...data),
        type
      )
    );
  };

  useEffect(() => {
    if (isModalLoaded.status) {
      setIsModalLoaded({ status: false });

      handleClearOptions('categories');
    }
  }, [isModalLoaded])

  const handleCloseModal = () => {
    handleModal(null, true);
  };

  const handleChangeTemplate = (template, folder) => {
    dispatch(setTemplateAction(template, folder))
      .then(() => {
        setIsTableHidden(false);
      });
  };

  const handleLoadSearchTemplate = () => {
    handleModal(
      <RadioListModal
        options={templates}
        templatesFolder={templatesFolder}
        selectedOption={template}
        title={t('templates')}
        closeModal={handleCloseModal}
        onChangeTemplate={handleChangeTemplate}
        handleOpenPremiumModal={handleOpenPremiumModal}
        subscreption={IS_PREMIUM}
      />,
      false,
      true
    );
  };

  const handleSaveTemplateModals = () => {
    const filterState = {
      categories: selectedCategories,
      properties: selectedProperties,
      group: selectedGroups,
    }

    handleModal(
      <SaveTemplateModals filterState={filterState} />,
      false,
      true
    );
  };

  const addParameterModalHandler = () => {
    handleModal(
      <AddParameterModal closeModal={handleCloseModal} />,
      false,
      true
    );
  }

  const handleChangeOptions = (option, optionsType) => {
    const updateCategoryRequest = (currentOptions) => {
      if (optionsType !== "categories") {
        return
      }

      dispatchFilters(updateCategory, "CATEGORY", currentOptions);
    };
    addPropertyHandler(option, optionsType, updateCategoryRequest)
  };

  const handleClearOptions = (optionsType) => {
    const updateCategoryRequest = () => {
      if (optionsType !== "categories") {
        return
      }

      clearColors();
      dispatchFilters(updateCategory, "CATEGORY", []);
    }
    removePropertyGroupHandler(optionsType, updateCategoryRequest)
  };

  const handleCreateReport = async () => {
    const modelId = getCurrentNamespace();
    
    localStorage.removeItem(`rowSelection:${modelId}`);

    dispatch(setCurrentTableStateAC({ selectedRows: [], modelId }));
    dispatch(addSelectItems({ selectItems: {}, id: 1, modelId }));
    await applyFiltesrs(filtersSchemes)
    await dispatch(updateTable(selectedProperties, selectedGroups));

    setIsTableHidden(false);
    clearColors()
  };

  const handleClosePremiumModal = () => {
    setPremiumModalVisible(false);
  };

  const handleOpenPremiumModal = () => {
    handleCloseModal();
    setPremiumModalVisible(true);
  };

  const scroll = () => {
    setInitDropdownIsOpen(!isOpenInitialDropdown);
  }

  const handleRemoveTag = (option, optionsType) => {
    const updateCategoryRequest = (currentOptions) => {
      if (optionsType !== "categories") {
        return
      }

      dispatchFilters(updateCategory, "CATEGORY", currentOptions);
    }
    addPropertyHandler(option, optionsType, updateCategoryRequest)
  };

  const functionSettings = tableColumns.filter((setting) => setting.metaData.isCustomColumn)

  const onRemoveAdvanceTagHandler = (settingName, isFunctionColumn = false) => {
    if (isFunctionColumn) {
      dispatch(deleteColumnAC(settingName, 1));
    }
    removeTableColumn(settingName, 1);
  };

  const getLabelWithCount = (label, count) => `${label}${count ? ` (${count})` : ''}`;

  return (
    <div className="sidebar-project__container">
      <div className="sidebar-project">
        <div className="sidebar-project__header">
          {t('search')}

          <div className="sidebar-project__header__actions">
            <button className="sidebar-project__header__actions__item" onClick={handleLoadSearchTemplate}>
              <FolderOpen />
            </button>

            <button className="sidebar-project__header__actions__item" onClick={IS_PREMIUM ? handleSaveTemplateModals : handleOpenPremiumModal}>
              <Save />
            </button>
          </div>
        </div>

        {i18n.language === "he" &&
          <TranslateModel
            isActive={isTranslateModelEnabled}
            onChange={setIsTranslateModelEnabled}
          />
        }

        <div className="sidebar-project__button-dropdown-wrapper" onScroll={scroll}>
          <div className="sidebar-project__button-dropdown-wrapper_scroll">
            <div className="sidebar-project__button-dropdown_container">
              <SidebarButtonDropdown
                label={getLabelWithCount(t('category'), selectedCategories.length)}
                buttonTitle={t('select_categories')}
                buttonBorder
                dropdownPosition="right"
                optionsType="categories"
                onClearAll={handleClearOptions}
                isOpenInitialDropdown={isOpenInitialDropdown}
              >
                <SidebarDropdown
                  optionsType="categories"
                  commonOptions={commonCategories}
                  uncommonOptions={uncommonCategories}
                  selectedItems={selectedCategories}
                  onChangeSelectedItems={handleChangeOptions}
                />
              </SidebarButtonDropdown>
            </div>

            <div className="sidebar-project__tags_container">
              {
                selectedCategories.map(option => {
                  const handleRemoveTagCategories = () => {
                    handleRemoveTag(option, "categories")
                  }
                  return (
                    <Tag
                      key={option}
                      title={getLabelByIsTranslateModelEnabled(option)}
                      onRemoveTag={handleRemoveTagCategories}
                    />
                  );
                })
              }
            </div>

            <div className="sidebar-project__button-dropdown_container">
              <SidebarButtonDropdown
                label={getLabelWithCount(t('table_parameters'), selectedProperties.length)}
                buttonTitle={t('select_parameters')}
                buttonBorder
                dropdownPosition="right"
                withAddBtn
                optionsType="properties"
                handleAddBtnClick={addParameterModalHandler}
                onClearAll={handleClearOptions}
                isOpenInitialDropdown={isOpenInitialDropdown}
              >
                <SidebarDropdown
                  optionsType="properties"
                  commonOptions={hasSelectedCategories ? commonProperties : []}
                  customOptions={hasSelectedCategories ? customPropertiesValues : []}
                  uncommonOptions={hasSelectedCategories ? uncommonProperties : []}
                  selectedItems={selectedProperties}
                  onChangeSelectedItems={handleChangeOptions}

                />
              </SidebarButtonDropdown>
            </div>

            <div className="sidebar-project__tags_container">
              {
                tableColumns?.map((setting) => {
                  const isActive = setting.metaData?.name === currentSettingColumnId && setting.metaData?.isSidebarOpen
                  const isColors = setting.settingsItems?.colors?.length
                  const isFilters = setting.settingsItems?.filters?.length
                  if (setting.metaData?.isCustomColumn) {
                    return null
                  }
                  const metaData = setting.metaData
                  const label = getLabelByIsTranslateModelEnabled(
                    metaData?.isCustomColumn ? setting.metaData.visibilityName : setting.metaData.name
                  )
                  return (
                    <AdvanceFilterTag
                      isActive={isActive}
                      key={setting.metaData?.name}
                      isColors={isColors}
                      isFilters={isFilters}
                      onRemoveHandler={() => onRemoveAdvanceTagHandler(setting.metaData?.name)}
                      onClick={() => openSideBarHandler(setting.metaData?.name)}
                      metaData={setting.metaData}
                      label={label}
                    />
                  );
                })
              }
            </div>

            <div className="sidebar-project__button-dropdown_container">
              <SidebarButtonDropdown
                label={getLabelWithCount(t('organize_by'), selectedGroups.length)}
                buttonTitle={t('select_groups')}
                buttonBorder
                dropdownPosition="right"
                optionsType="groups"
                onClearAll={handleClearOptions}
                isOpenInitialDropdown={isOpenInitialDropdown}
              >
                <SidebarDropdown
                  optionsType="groups"
                  commonOptions={commonGroupingOptions}
                  customOptions={hasSelectedCategories ? customPropertiesValues : []}
                  uncommonOptions={uncommonGroupingOptions}
                  selectedItems={selectedGroups}
                  onChangeSelectedItems={handleChangeOptions}
                  hasSelectedCategories={hasSelectedCategories}
                />
              </SidebarButtonDropdown>
            </div>

            <div className="sidebar-project__tags_container">
              {
                selectedGroups.map(option => {
                  const handleRemoveTagGroup = () => {
                    handleRemoveTag(option, "groups")
                  }
                  return (
                    <Tag
                      key={option}
                      title={getLabelByIsTranslateModelEnabled(option)}
                      onRemoveTag={handleRemoveTagGroup}
                    />
                  );
                })
              }
            </div>

            {
              !!functionSettings?.length ? <div>
                <div className="sidebar-project__section_title">{t('functions')}</div>

                <div>
                  {
                    functionSettings?.map((setting) => {
                      const isActive = setting.metaData?.name === currentSettingColumnId
                      const metaData = setting.metaData
                      const label = getLabelByIsTranslateModelEnabled(
                        metaData?.isCustomColumn ? setting.metaData.visibilityName : setting.metaData.name
                      )
                      return (
                        <AdvanceFilterTag
                          key={setting.metaData?.name}
                          isActive={isActive}
                          onRemoveHandler={() => onRemoveAdvanceTagHandler(setting.metaData?.name, true)}
                          onClick={() => openSideBarHandler(setting.metaData?.name)}
                          metaData={setting.metaData}
                          label={label}
                        />
                      );
                    })
                  }
                </div>
              </div> : null
            }
          </div>
        </div>
      </div>

      <div className="sidebar-module__footer">
        <UniversalButton
          label={t('create_report')}
          size="lg"
          labelColor="white"
          labelHoverColor="white"
          backgroundColor="orange"
          backgroundHoverColor="orange-light"
          isFullWidth
          onClick={handleCreateReport}
        />
      </div>

      <ModalComponent
        onCloseModal={handleClosePremiumModal}
        isVisible={isPremiumModalVisible}
      >
        <GoToPremiumModal
          text="<p>Premium version allows you to save templates endlessly, save elements as you wish, and much more.</p>
                <br/>
                <p>Take a look at our site to best understand what the premium upgrade has to offer!</p>"
        />
      </ModalComponent>
    </div>
  );
};

export default SidebarProject;
